import * as Sentry from '@sentry/angular';
import { browserSessionIntegration, browserTracingIntegration, replayIntegration } from '@sentry/angular';
import { AppConfig } from '../app/providers/config.service';
import { Profile } from '../app/providers/auth.service';
import { RELEASE } from '../version';
import { environment } from '../environments/environment';

export function setLocalStorageInSentryContext(): void {
    try {
        const orgInfo = localStorage.getItem('chargeye-organization-info');
        if (orgInfo) {
            try {
                const parsedOrgInfo = JSON.parse(orgInfo);
                Sentry.setContext('organization', parsedOrgInfo);
            } catch {
                Sentry.setContext('organization', { value: orgInfo });
            }
        }

        const featureFlags = localStorage.getItem('chargeye-feature-flags');
        if (featureFlags) {
            try {
                const parsedFlags = JSON.parse(featureFlags);
                Sentry.setContext('feature-flags', parsedFlags);
            } catch {
                Sentry.setContext('feature-flags', { value: featureFlags });
            }
        }

        const devFeatures = localStorage.getItem('chargeye-development-features');
        if (devFeatures) {
            Sentry.setContext('development-features', { value: devFeatures });
        }
    } catch (err) {
        console.warn('Failed to set localStorage values in Sentry context', err);
    }
}

/**
 * Global hook for AuthService to provide its profile to Sentry.
 * This allows late binding after Angular DI is ready.
 */
let authProfileGetter: (() => Profile | null) | null = null;

/**
 * Register a function that returns the current AuthService profile.
 * Should be called by AuthService during initialization.
 *
 * @param getter - Function that returns the current profile
 *
 * @example
 * // In AuthService constructor:
 * import { registerAuthProfileGetter } from '../sentry/init';
 *
 * constructor() {
 *     // Register getter so Sentry can access the profile
 *     registerAuthProfileGetter(() => this.profile);
 * }
 */
export function registerAuthProfileGetter(getter: () => Profile | null): void {
    authProfileGetter = getter;
}

/**
 * Set authentication context in Sentry from AuthService profile.
 * Tries to get profile from registered getter, then falls back to localStorage.
 *
 * @param profile - Optional AuthService profile object (for direct calls)
 */
export function setAuthContextInSentry(profile?: Profile): void {
    try {
        // Priority 1: Use provided profile (direct call)
        let authProfile = profile;

        // Priority 2: Try to get profile from registered AuthService getter
        if (!authProfile && authProfileGetter) {
            try {
                authProfile = authProfileGetter() || undefined;
            } catch (err) {
                console.warn('Failed to get profile from AuthService', err);
            }
        }

        if (authProfile) {
            // Use AuthService profile data when available
            Sentry.setContext('auth', {
                tenantId: authProfile.tenantId,
                userRole: authProfile.userRole,
                systemRoles: authProfile.systemRoles || [],
                impersonated: authProfile.impersonated || false,
            });
        } else {
            // Priority 3: Fall back to reading from localStorage
            const authInfo = localStorage.getItem('kempower-csms-auth');
            if (authInfo) {
                try {
                    const parsedAuthInfo = JSON.parse(authInfo);
                    Sentry.setContext('auth', {
                        tenantId: parsedAuthInfo.tenantId,
                        userRole: parsedAuthInfo.userRole,
                        systemRoles: parsedAuthInfo.systemRoles || parsedAuthInfo.roles || [],
                        impersonated: parsedAuthInfo.impersonated || false,
                    });
                } catch (err) {
                    console.warn('Failed to parse kempower-csms-auth from localStorage', err);
                }
            }
        }
    } catch (err) {
        console.warn('Failed to set auth context in Sentry', err);
    }
}

export function initSentry(config: AppConfig): void {
    Sentry.init({
        dsn: config.sentryDsn,
        environment: config.envName,
        release: RELEASE,
        integrations: [browserTracingIntegration(), browserSessionIntegration(), replayIntegration()],
        tracesSampler: (samplingContext) => {
            const transactionName = samplingContext.transactionContext?.name || '';

            if (transactionName.toLowerCase().includes('n+1 api call')) {
                return 0.001;
            }

            return environment.production ? 0.01 : 0.5;
        },
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: config.sentryReplaySampleRate,
        ignoreErrors: ['ResizeObserver loop limit exceeded', /ERR_BLOCKED_BY_CLIENT/],
        beforeSend(event) {
            // Filter out errors from browser extensions
            if (event.exception) {
                const frames = event.exception.values?.[0]?.stacktrace?.frames;
                if (
                    frames &&
                    frames.some(
                        (frame) =>
                            frame.filename?.includes('extension://') ||
                            frame.filename?.includes('chrome-extension://') ||
                            frame.filename?.includes('moz-extension://')
                    )
                ) {
                    return null;
                }
            }

            setLocalStorageInSentryContext();
            setAuthContextInSentry();
            return event;
        },
        beforeSendTransaction(event) {
            // Drop health check / polling transactions that don't provide value
            // but keep them if they represent errors
            const transactionName = event.transaction || '';
            const status = event.contexts?.trace?.status;
            const isSuccessful = status === 'ok';

            if (transactionName.includes('config.json') && isSuccessful) {
                return null;
            }
            return event;
        },
    });
}
